/**
 * Layer roles — base vs overlay taxonomy for compound / stacked rendering.
 *
 * (See docs/CONVERSATIONAL-AND-COMPOUND-ROADMAP.md, Phase 2.)
 *
 * EarthVoice's render engine clears the map on every op. To support the
 * "one base choropleth + stackable overlays" model, the compound executor needs
 * to know, for each step, whether the step is:
 *
 *   - 'base'    — a full-globe area fill (choropleth, bivariate, h3, categorical).
 *                 Two of these cannot coexist (they fight for the same pixels),
 *                 so a new base REPLACES the current base.
 *   - 'overlay' — an additive layer that sits ON TOP of a base: point markers,
 *                 proportional symbols, POI, an isochrone polygon, a deviational
 *                 ellipse, a center point, gravity flow lines, neighbor highlights.
 *                 These PRESERVE the base and stack.
 *
 * The render dispatcher (src/ai/ops/duckdb-query.js) branches primarily on
 * `intent.visualization.type`, so that is the most reliable signal; we fall
 * back to the intent type / forced tool, then the pipeline op name.
 *
 * DEFAULT IS 'base'. An unknown descriptor behaves exactly as the engine does
 * today (clear-and-replace), so this classifier can never make an unknown op
 * silently start stacking. Pure + unit-tested; nothing here renders or mutates.
 */

// Full-area fills — these replace the current base.
const BASE_VIZ = new Set([
  'choropleth',
  'bivariate_choropleth',
  'fill',
  'h3-density',
  'h3_density',
  'categorical',
  'hexbin',
])

// Additive overlays — these stack on top of a base.
const OVERLAY_VIZ = new Set([
  'poi-markers',
  'proportional-symbol',
  'points',
  'point',
  'markers',
  'marker',
  'lines',
  'line',
  'arc',
  'flow',
  'isochrone',
  'ellipse',
  'heat',
  'heatmap',
])

// Op / tool names whose output is an additive overlay regardless of viz hints.
const OVERLAY_OPS = new Set([
  'center',
  'centrography',
  'proximity',
  'isochrone',
  'routing',
  'gravity',
  'neighbors',
  'forced_poi',
  'poi',
  'ellipse',
  'deviational_ellipse',
  'overlay_cities',
])

// Forced-tool / intent-type names (geo_*) that are overlays.
const OVERLAY_TOOLS = new Set([
  'geo_center',
  'geo_centrography',
  'geo_proximity',
  'geo_neighbors',
  'geo_gravity',
  'geo_isochrone',
  'geo_ellipse',
])

function norm(s) {
  return String(s == null ? '' : s)
    .toLowerCase()
    .trim()
}

/** Classify a bare token (viz type, op, or tool name). Exported for testing. */
export function roleOfToken(token) {
  const t = norm(token)
  if (!t) return 'base'
  if (OVERLAY_VIZ.has(t) || OVERLAY_OPS.has(t) || OVERLAY_TOOLS.has(t)) return 'overlay'
  return 'base'
}

/**
 * Classify an intent (or a bare token) as 'base' or 'overlay'.
 * Order of evidence: visualization.type → intent type / forced tool →
 * pipeline op name. Defaults to 'base' (current clear-and-replace behavior).
 */
export function layerRole(descriptor) {
  if (descriptor == null) return 'base'
  if (typeof descriptor === 'string') return roleOfToken(descriptor)

  const intent = descriptor

  // 1) Visualization type — the signal the render dispatcher actually branches on.
  const viz = norm(intent.visualization?.type)
  if (viz) {
    if (OVERLAY_VIZ.has(viz)) return 'overlay'
    if (BASE_VIZ.has(viz)) return 'base'
  }

  // 2) Intent type / forced tool.
  const type = norm(intent.type || intent._tool)
  if (type) {
    if (OVERLAY_TOOLS.has(type) || OVERLAY_OPS.has(type.replace(/^geo_/, ''))) return 'overlay'
  }

  // 3) Pipeline op name (first op) or a bare `op` field.
  const op = norm(intent.op || (Array.isArray(intent.pipeline) && intent.pipeline[0]?.op))
  if (op && OVERLAY_OPS.has(op)) return 'overlay'

  return 'base'
}

/** Convenience predicate. */
export function isOverlay(descriptor) {
  return layerRole(descriptor) === 'overlay'
}

const layerRoles = { layerRole, roleOfToken, isOverlay }
export default layerRoles
